import { WELCOME } from '@sonder/shared'
import { useAuth } from '@/auth/AuthProvider'
import { useMembers } from '@/data/client'
import { useDoc } from '@/data/hooks'
import { ButtonLink, Eyebrow, Hero } from '@/ui'
import { TopBar } from './TopBar'
import { welcomeCopy } from './welcomeCopy'
import type { PageProps } from './ClientRoutes'

export function WelcomePage({ clientId, client, config, base }: PageProps) {
  const { user } = useAuth()
  const members = useMembers(clientId)
  const brands = useDoc<{ order: string[]; items: Record<string, { name: { value: string } }> }>(`clients/${clientId}/sections/brands`)
  const brandNames = (brands.data?.order ?? []).map((id) => brands.data?.items[id]?.name.value).filter((n): n is string => Boolean(n))
  const ordered = [...members.items].sort((a, b) => (a.role === b.role ? (a.name || a.email).localeCompare(b.name || b.email) : a.role === 'owner' ? -1 : 1))
  const copy = welcomeCopy({ clientName: client.name, config, memberNames: ordered.map((m) => m.name || m.email), brandNames })

  return (
    <>
      <TopBar clientName={client.name} section="Welcome" homeHref={base} />
      <main id="main">
        <Hero
          media={config.welcomeMedia}
          eyebrow={copy.eyebrow}
          title={
            <>
              {copy.headline}
              <br />
              {copy.subhead}
            </>
          }
          actions={
            <>
              <ButtonLink to={`${base}/team`} variant="inverse">
                {WELCOME.begin}
              </ButtonLink>
              <ButtonLink to={`${base}/people`} variant="ghost-inverse" arrow={false}>
                Invite a teammate
              </ButtonLink>
            </>
          }
        >
          <p>{copy.lead}</p>
        </Hero>
        <section className="mx-auto grid max-w-portal gap-8 px-6 py-section-sm md:grid-cols-2 md:px-10">
          <div className="grid content-start gap-3">
            <Eyebrow>{WELCOME.signedInAs}</Eyebrow>
            <p className="text-h4">{user?.displayName || user?.email}</p>
            <p className="text-small text-dim">{WELCOME.signedInHint} Not you? Sign out from the top right.</p>
          </div>
          <div className="grid content-start gap-3">
            <Eyebrow>{WELCOME.timeTitle}</Eyebrow>
            <p className="text-small text-charcoal">{copy.duration}</p>
          </div>
          {copy.scopeNotes && (
            <div className="grid content-start gap-3 md:col-span-2">
              <Eyebrow>{WELCOME.scopeTitle}</Eyebrow>
              <p className="max-w-2xl text-small text-charcoal">{copy.scopeNotes}</p>
            </div>
          )}
        </section>
      </main>
    </>
  )
}
